import { OptionAdornment, OptionIcon } from '@app/src/components/Form/Select'
import SelectPaper from '@app/src/components/Form/Select/SelectPaper'
import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import TextField from '@app/src/components/Ui/TextField'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { AutocompleteProps, TextFieldProps, ZIndex } from '@mui/material'
import { Autocomplete, Box, Chip, Tooltip } from '@mui/material'
import React, { useCallback, useState } from 'react'

export interface Option {
  label: string
  value: string | number | boolean
  adornment?: string
  additionalText?: string
}

export const getAutocompleteHeightControlStyles = (zIndex: ZIndex) => ({
  '&.Mui-focused': {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: zIndex.drawer + 1,
  },
  '&:not(.Mui-focused)': {
    height: 40,
    '& .MuiAutocomplete-tag': {
      maxWidth: 'calc(100% - 24px)',
    },
    '& .MuiInputBase-root': {
      height: 40,
      flexWrap: 'nowrap',
    },
  },
})

type RawAutocompleteProps = AutocompleteProps<Option, boolean, false, false, 'div'>

type ForwardedAutocompleteProps = Omit<
  RawAutocompleteProps,
  | 'renderInput'
  | 'classes'
  | 'PaperComponent'
  | 'popupIcon'
  | 'open'
  | 'onOpen'
  | 'onClose'
  | 'onFocus'
  | 'sx'
  | 'clearOnBlur'
  | 'limitTags'
  | 'getOptionLabel'
  | 'multiple'
  | 'disableCloseOnSelect'
  | 'renderOption'
  | 'renderTags'
>

interface FilterAutocompleteOwnProps {
  fieldLabel: string
  required?: boolean
  variant?: TextFieldProps['variant']
  helperText?: string
  actionButtons?: JSX.Element
  modifiers?: JSX.Element
  multiple?: boolean
  disableCloseOnSelect?: boolean
  renderOption?: RawAutocompleteProps['renderOption']
  renderTags?: RawAutocompleteProps['renderTags']
}

export type FilterAutocompleteProps = ForwardedAutocompleteProps & FilterAutocompleteOwnProps

function FilterAutocomplete({
  fieldLabel,
  required,
  variant = 'outlined',
  helperText,
  actionButtons,
  modifiers,
  multiple,
  renderOption,
  renderTags,
  disableCloseOnSelect,
  ...autocompleteProps
}: FilterAutocompleteProps) {
  const classes = useAutocompleteStyles()
  const [open, setOpen] = useState(false)

  const PaperComponent = useCallback(
    ({ children }: React.HTMLAttributes<HTMLElement>) => (
      <SelectPaper actionButtons={actionButtons} modifiers={modifiers}>
        {children}
      </SelectPaper>
    ),
    [actionButtons, modifiers],
  )

  const defaultRenderOption: FilterAutocompleteOwnProps['renderOption'] = (optionProps, option, { selected }) => {
    const { key: _omitKey, ...rest } = optionProps as Omit<typeof optionProps, 'key'> & { key?: React.Key }
    return (
      <li key={option.value.toString()} {...rest}>
        <OptionIcon selected={selected} multiple={Boolean(multiple)} />
        {option?.label || ''}
        <OptionAdornment option={option} />
      </li>
    )
  }

  const defaultRenderTags: FilterAutocompleteOwnProps['renderTags'] = (value, getTagProps) =>
    value?.map(
      (option, index) =>
        option && (
          <Tooltip
            key={option?.value?.toString() || index}
            title={option.label}
            classes={{ tooltipPlacementBottom: classes.tooltip }}
            arrow
          >
            <Chip label={option.label} size="small" {...getTagProps({ index })} />
          </Tooltip>
        ),
    ) ?? []

  return (
    <Box sx={{ position: 'relative', height: 40 }}>
      <Autocomplete
        data-testid={`autocomplete-filter-${fieldLabel}`}
        {...autocompleteProps}
        multiple={multiple}
        clearOnBlur={false}
        limitTags={1}
        disableCloseOnSelect={disableCloseOnSelect ?? Boolean(multiple)}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        sx={({ zIndex }) => getAutocompleteHeightControlStyles(zIndex)}
        getOptionLabel={option => option.label}
        renderInput={params => (
          <TextField variant={variant} label={fieldLabel} helperText={helperText} required={required} {...params} />
        )}
        renderOption={renderOption ?? defaultRenderOption}
        renderTags={renderTags ?? defaultRenderTags}
        classes={{
          clearIndicator: classes.clearIndicator,
          popupIndicator: classes.popupIndicator,
          paper: classes.paper,
        }}
        popupIcon={<ExpandMoreIcon />}
        PaperComponent={PaperComponent}
      />
    </Box>
  )
}

export default FilterAutocomplete
